import { apiFetch, buildApiError } from './httpClient';
/**
 * Plaid service for API communication
 */

let plaidScriptPromise = null;

async function parsePlaidResponse(response, fallbackMessage) {
  let result = null;
  try {
    result = await response.json();
  } catch (_) {
    if (!response.ok) throw await buildApiError(response, fallbackMessage);
    const error = new Error(fallbackMessage);
    error.status = response.status;
    throw error;
  }

  if (!response.ok) {
    const error = new Error(result?.detail?.message || result?.error || fallbackMessage);
    error.status = response.status;
    error.detail = result?.detail || result?.error || result;
    if (error.detail?.code) error.code = error.detail.code;
    throw error;
  }

  if (!result.ok) {
    const error = new Error(result?.error || fallbackMessage);
    error.status = response.status;
    error.detail = result?.error || result;
    throw error;
  }

  return result.data;
}

export function loadPlaidScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Plaid Link requires a browser environment'));
  }

  if (window.Plaid) {
    return Promise.resolve(true);
  }

  if (plaidScriptPromise) {
    return plaidScriptPromise;
  }

  plaidScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-plaid-link]');
    if (existingScript) {
      if (existingScript.dataset.plaidLoaded === 'true') {
        resolve(true);
        return;
      }

      existingScript.addEventListener('load', () => resolve(true), { once: true });
      existingScript.addEventListener(
        'error',
        () => {
          plaidScriptPromise = null;
          reject(new Error('Failed to load Plaid Link'));
        },
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    script.async = true;
    script.dataset.plaidLink = 'true';
    script.onload = () => {
      script.dataset.plaidLoaded = 'true';
      resolve(true);
    };
    script.onerror = () => {
      plaidScriptPromise = null;
      reject(new Error('Failed to load Plaid Link'));
    };
    document.body.appendChild(script);
  });

  return plaidScriptPromise;
}

/**
 * Create a Plaid Link token
 * @param {('bank_credit'|'investment'|'loan_mortgage')} accountType
 * @returns {Promise<{link_token: string, expiration: string}>}
 */
export async function createLinkToken(accountType = 'bank_credit') {
  const response = await apiFetch('/api/plaid/link-token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ account_type: accountType }),
  });

  return parsePlaidResponse(response, 'Failed to create link token');
}

/**
 * Create a Plaid Link token for update mode (re-authentication)
 * @param {string} itemId - The Plaid item ID to update
 * @returns {Promise<{link_token: string, expiration: string}>}
 */
export async function createUpdateLinkToken(itemId) {
  const response = await apiFetch('/api/plaid/link-token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      account_type: 'bank_credit', // Ignored in update mode
      item_id: itemId
    }),
  });

  return parsePlaidResponse(response, 'Failed to create update link token');
}

/**
 * Exchange public token for access token
 * @param {string} publicToken - Public token from Plaid Link
 * @returns {Promise<{item_id: string, institution_id: string, institution_name: string}>}
 */
export async function exchangePublicToken(publicToken) {
  const response = await apiFetch('/api/plaid/exchange-token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ public_token: publicToken }),
  });

  return parsePlaidResponse(response, 'Failed to exchange token');
}

/**
 * Sync Plaid data for all items or a specific item
 * @param {string|null} itemId - Optional item ID to sync specific item
 * @returns {Promise<Object>}
 */
export async function syncPlaidData(itemId = null) {
  const response = await apiFetch('/api/plaid/sync', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ item_id: itemId }),
  });

  return parsePlaidResponse(response, 'Failed to sync data');
}

/**
 * Get all Plaid items (connections) for the user
 * @returns {Promise<Array>}
 */
export async function getPlaidItems() {
  const response = await apiFetch('/api/plaid/items', {
    method: 'GET',
  });

  return (await parsePlaidResponse(response, 'Failed to fetch items')) || [];
}

/**
 * Get all Plaid accounts for the user
 * @returns {Promise<Array>}
 */
export async function getPlaidAccounts() {
  const response = await apiFetch('/api/plaid/accounts', {
    method: 'GET',
  });

  return (await parsePlaidResponse(response, 'Failed to fetch accounts')) || [];
}

/**
 * Get Plaid transactions
 * @param {Object} options - Query options
 * @param {string} options.startDate - Start date (ISO format)
 * @param {string} options.endDate - End date (ISO format)
 * @param {number} options.limit - Number of transactions to fetch
 * @param {number} options.offset - Offset for pagination
 * @returns {Promise<Array>}
 */
export async function getPlaidTransactions({ startDate, endDate, limit = 100, offset = 0 } = {}) {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  params.append('limit', limit);
  params.append('offset', offset);

  const response = await apiFetch(`/api/plaid/transactions?${params}`, {
    method: 'GET',
  });

  return (await parsePlaidResponse(response, 'Failed to fetch transactions')) || [];
}

/**
 * Get Plaid investment transactions
 * @param {Object} options - Query options
 * @param {string} options.startDate - Start date (ISO format)
 * @param {string} options.endDate - End date (ISO format)
 * @param {number} options.limit - Number of transactions to fetch
 * @param {number} options.offset - Offset for pagination
 * @returns {Promise<Array>}
 */
export async function getPlaidInvestmentTransactions({ startDate, endDate, limit = 100, offset = 0 } = {}) {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  params.append('limit', limit);
  params.append('offset', offset);

  const response = await apiFetch(`/api/plaid/investment-transactions?${params}`, {
    method: 'GET',
  });

  return (await parsePlaidResponse(response, 'Failed to fetch investment transactions')) || [];
}

/**
 * Get Plaid investment holdings
 * @returns {Promise<Array>}
 */
export async function getPlaidHoldings() {
  const response = await apiFetch('/api/plaid/holdings', {
    method: 'GET',
  });

  return (await parsePlaidResponse(response, 'Failed to fetch holdings')) || [];
}

/**
 * Get Plaid liabilities
 * @returns {Promise<Array>}
 */
export async function getPlaidLiabilities() {
  const response = await apiFetch('/api/plaid/liabilities', {
    method: 'GET',
  });

  return (await parsePlaidResponse(response, 'Failed to fetch liabilities')) || [];
}

/**
 * Get Plaid connection status
 * @returns {Promise<Object>}
 */
export async function getPlaidStatus() {
  const response = await apiFetch('/api/plaid/status', {
    method: 'GET',
  });

  return parsePlaidResponse(response, 'Failed to fetch status');
}

/**
 * Disconnect a Plaid item
 * @param {string} itemId - Item ID to disconnect
 * @returns {Promise<Object>}
 */
export async function disconnectPlaidItem(itemId) {
  const response = await apiFetch(`/api/plaid/items/${itemId}`, {
    method: 'DELETE',
  });

  return parsePlaidResponse(response, 'Failed to disconnect item');
}

/**
 * Sync all Plaid items (background sync)
 * @returns {Promise<void>}
 */
export async function syncAllItems() {
  try {
    await syncPlaidData(null);
  } catch (error) {
    console.error('Background sync failed:', error);
    // Don't throw - this is a background operation
  }
}
